import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FixedSizeList } from 'react-window';
import { useResponsive } from 'hooks';
import { useFilteredOptions } from './use-filtered-options';
import { ISelectValue, SelectProps } from '../select.types';

type SelectModelProps = SelectProps & {
  requiredPlaceholderMark?: boolean;
};

/** Хук модели для Select — управляет состоянием, логикой выбора и фильтрацией */
export const useSelect = ({
  closeOnScroll = true,
  closeOnSelect = false,
  searchable = true,
  clearable = true,
  ...props
}: SelectModelProps) => {
  const responsive = useResponsive();
  // ─────────────────────────────────────────────────────────────────────────────
  // Refs для доступа к DOM-элементам
  // ─────────────────────────────────────────────────────────────────────────────
  const triggerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<FixedSizeList>(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // Состояния
  // ─────────────────────────────────────────────────────────────────────────────
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [highlightedIndex, setHighlightedIndex] = useState<number>(0);
  const [recentlySelectedValue, setRecentlySelectedValue] = useState<
    string | null
  >(null);

  const isMulti = !!props.multi;

  const { filteredOptions: rawFilteredOptions } = useFilteredOptions(
    props.options,
    search
  );

  const filteredOptions =
    props.hideSelected && props.multi
      ? rawFilteredOptions.filter((opt) => !props.value.includes(opt.value))
      : rawFilteredOptions;

  const firstAvailableOptionIndex = filteredOptions.findIndex(
    (opt) => !opt.isDisabled
  );
  const lastAvailableOptionIndex = filteredOptions.reduce(
    (last, opt, i) => (!opt.isDisabled ? i : last),
    -1
  );
  const fullDisabled = props.disabled || props.loading || props.readOnly;

  const scrollToOption = (index?: number) => {
    listRef.current?.scrollToItem?.(index ?? highlightedIndex, 'smart');
  };

  // Сброс недавно выбранного значения при первом рендере
  useLayoutEffect(() => {
    if (!recentlySelectedValue) {
      return;
    }

    const id = setTimeout(() => {
      setRecentlySelectedValue(null);
    }, 200);

    return () => clearTimeout(id);
  }, [recentlySelectedValue]);

  // Автофокус на поле ввода при открытии
  useEffect(() => {
    if (isOpen) {
      searchRef.current?.focus();

      const firstSelectedValue = isMulti ? props.value[0] : props.value;

      const index = filteredOptions.findIndex(
        (opt) => !opt.isDisabled && opt.value === firstSelectedValue
      );
      const newIndex =
        index >= 0
          ? index
          : firstAvailableOptionIndex >= 0
            ? firstAvailableOptionIndex
            : 0;

      setHighlightedIndex(newIndex);
      scrollToOption(newIndex);
    }
  }, [isOpen]);

  // Закрытие дропдауна и сброс поиска
  const handleCloseDropdown = () => {
    if (fullDisabled) {
      return;
    }

    setSearch('');
    setIsOpen(false);
  };

  // Toggle дропдауна и сброс поиска при закрытии
  const handleToggleDropdown = () => {
    if (fullDisabled) {
      return;
    }

    if (isOpen) {
      setSearch('');
    }

    setIsOpen((prev) => !prev);
  };

  // Обработка выбора опции
  const handleSelect = (currentOption: ISelectValue) => {
    if (props.disabled || currentOption.isDisabled) {
      return;
    }

    setSearch('');

    if (isMulti) {
      const exists = props.value.includes(currentOption.value);
      const indexInOptionsList = filteredOptions.findIndex(
        (el) => el.value === currentOption.value
      );
      let next: string[];

      if (exists) {
        next = props.value.filter((v) => v !== currentOption.value);
      } else {
        next = [...props.value, currentOption.value];
      }

      if (indexInOptionsList >= 0) {
        setHighlightedIndex(indexInOptionsList);
      }

      props.onChange(next);

      if (closeOnSelect) {
        setIsOpen(false);
        searchRef.current?.blur();
      } else {
        setIsOpen(true);
        searchRef.current?.focus();
      }
    } else if (currentOption.value !== props.value) {
      props.onChange(currentOption.value);
      setIsOpen(false);
      searchRef.current?.blur();
    }

    setRecentlySelectedValue(currentOption.value);
  };

  // Удаление одной выбранной опции
  const handleRemove = (value: string) => {
    if (!isMulti || props.disabled) {
      return;
    }

    if (isOpen) {
      searchRef.current?.focus();
    }

    props.onChange(props.value.filter((v) => v !== value));
  };

  const handleRemoveLastOption = () => {
    if (!isMulti || props.disabled) {
      return;
    }

    props.onChange(props.value.slice(0, -1));
  };

  // Очистка всех выбранных опций
  const handleRemoveAll = () => {
    if (props.disabled) {
      return;
    }

    if (isOpen) {
      searchRef.current?.focus();
    }

    if (isMulti) {
      props.onChange([]);
    } else {
      props.onChange(null);
    }

    setSearch('');
  };

  // Выбранные опции (с сортировкой по порядку выбора в multi)
  const labelPool = props.selectedValueOptions
    ? [...props.selectedValueOptions, ...props.options]
    : props.options;
  const selectedOptions: ISelectValue[] = isMulti
    ? (props.value
        .map((val) => labelPool.find((opt) => opt.value === val))
        .filter(Boolean) as ISelectValue[])
    : labelPool.filter((opt) => opt.value === props.value);

  const handleNavigateUp = () => {
    setHighlightedIndex((i) => {
      if (filteredOptions.length === 0) {
        return 0;
      }

      let newIndex = i;

      for (let step = 0; step < filteredOptions.length; step++) {
        newIndex =
          (newIndex - 1 + filteredOptions.length) % filteredOptions.length;

        if (!filteredOptions[newIndex].isDisabled) {
          break;
        }
      }

      scrollToOption(newIndex);

      return newIndex;
    });
  };

  const handleNavigateDown = () => {
    setHighlightedIndex((i) => {
      if (filteredOptions.length === 0) {
        return 0;
      }

      let newIndex = i;

      for (let step = 0; step < filteredOptions.length; step++) {
        newIndex = (newIndex + 1) % filteredOptions.length;

        if (!filteredOptions[newIndex].isDisabled) {
          break;
        }
      }

      scrollToOption(newIndex);

      return newIndex;
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    event.stopPropagation();

    const { key } = event;

    if (!isOpen && (key === 'Enter' || key === 'ArrowDown')) {
      actions.openDropdown();

      return;
    }

    switch (key) {
      case 'ArrowDown':
        handleNavigateDown();
        break;
      case 'ArrowUp':
        handleNavigateUp();
        break;
      case 'Home':
        event.preventDefault();

        if (firstAvailableOptionIndex >= 0) {
          actions.highlight(firstAvailableOptionIndex);
          scrollToOption(firstAvailableOptionIndex);
        }

        break;
      case 'End':
        event.preventDefault();

        if (lastAvailableOptionIndex >= 0) {
          actions.highlight(lastAvailableOptionIndex);
          scrollToOption(lastAvailableOptionIndex);
        }

        break;
      case 'Enter':
        const selectedOption = filteredOptions[highlightedIndex];

        if (selectedOption && !selectedOption.isDisabled) {
          const wasAlreadySelected = isMulti
            ? props.value.includes(selectedOption.value)
            : props.value === selectedOption.value;

          actions.select(selectedOption);

          if (!props.multi || closeOnSelect) {
            actions.closeDropdown();
          } else {
            searchRef.current?.focus();

            if (!wasAlreadySelected) {
              let nextIndex = highlightedIndex + 1;

              while (
                nextIndex < filteredOptions.length &&
                filteredOptions[nextIndex].isDisabled
              ) {
                nextIndex++;
              }

              if (nextIndex < filteredOptions.length) {
                setHighlightedIndex(nextIndex);
                scrollToOption(nextIndex);
              }
            }
          }
        }

        break;
      case 'Escape':
        actions.closeDropdown();

        if (!props.multi) {
          searchRef.current?.blur();
        }

        break;
      case 'Tab':
        actions.closeDropdown();
        break;
      default:
        break;
    }
  };

  const handleSetSearch = (value: string) => {
    setSearch(value);
    props.onSearchChange?.(value);
  };

  // Методы действий
  const actions = {
    openDropdown: () => !fullDisabled && setIsOpen(true),
    closeDropdown: handleCloseDropdown,
    toggleDropdown: handleToggleDropdown,
    select: handleSelect,
    remove: handleRemove,
    removeAll: handleRemoveAll,
    removeLastOption: handleRemoveLastOption,
    highlight: setHighlightedIndex,
    handleKeyDown,
    setSearch: handleSetSearch,
  };

  // Возврат модели
  return {
    refs: { triggerRef, dropdownRef, searchRef, listRef },
    state: {
      ...props,
      searchable,
      clearable,
      closeOnScroll,
      closeOnSelect,
      isOpen,
      highlightedIndex,
      filteredOptions,
      selectedOptions,
      search,
      fullDisabled,
      hasAvailableOptions: filteredOptions.some((opt) => !opt.isDisabled),
      recentlySelectedValue,
      responsive,
    },
    actions,
  };
};
